import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../i18n';
import { C } from '../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge } from '../../../lib/ui';
import { buscar } from '../services/api';
import type { DocumentoResumen } from '../../../types';

interface Props {
  onClose: () => void;
}

export function QuickSearchModal({ onClose }: Props) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DocumentoResumen[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      setLoading(true);
      buscar(query)
        .then((res) => setResults(res.data.slice(0, 8)))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  function handleSelect(doc: DocumentoResumen) {
    onClose();
    navigate(`/documento/${doc.codigo}`);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '15vh',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxHeight: 400,
          backgroundColor: C.surface,
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Search input row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <Ic d={ICON_PATHS.search} s={18} c={C.textMut} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.quickSearch.placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'Enter' && results.length > 0) handleSelect(results[0]);
            }}
            style={{
              flex: 1,
              fontSize: 15,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              color: C.text,
              fontFamily: 'inherit',
            }}
          />
          <span
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 4,
              backgroundColor: C.border,
              color: C.textMut,
              fontFamily: 'inherit',
            }}
          >
            ESC
          </span>
        </div>

        {/* Results area */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <div
              style={{ padding: '24px 18px', textAlign: 'center', fontSize: 13, color: C.textMut }}
            >
              {t.search.searching}
            </div>
          )}

          {!loading && query && results.length === 0 && (
            <div
              style={{ padding: '24px 18px', textAlign: 'center', fontSize: 13, color: C.textMut }}
            >
              {t.search.noResultsFound}
            </div>
          )}

          {!loading && results.length > 0 && (
            <>
              <div
                style={{
                  padding: '10px 18px 4px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: C.textMut,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                {t.search.recentDocs}
              </div>
              {results.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => handleSelect(doc)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '10px 18px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = C.bg;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  }}
                >
                  <Ic d={ICON_PATHS.doc} s={16} c={C.textMut} />
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.azul1,
                      flexShrink: 0,
                    }}
                  >
                    {doc.codigo}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: C.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doc.titulo}
                  </span>
                  <StatusBadge status={doc.estado} small />
                </button>
              ))}
            </>
          )}

          {!query && (
            <div
              style={{
                padding: '18px',
                fontSize: 12,
                color: C.textMut,
                textAlign: 'center',
              }}
            >
              {t.search.typeToSearch}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
